import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import { Check, Calendar, Heart, Sparkles, Home, ExternalLink } from 'lucide-react';
import Confetti from 'react-confetti';
import Logo from '../components/common/Logo';
import MoonIcon from '../components/common/MoonIcon';
import { Button } from '../components/ui/button';

const CompletionPage = () => {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const [showConfetti, setShowConfetti] = useState(true);
  const [windowSize, setWindowSize] = useState(() => {
    // Acesso seguro ao window durante inicialização
    if (typeof window !== 'undefined') {
      return { width: window.innerWidth, height: window.innerHeight };
    }
    return { width: 1024, height: 768 }; // Fallback
  });

  const MEMBERS_AREA_URL = 'https://mentoria.marimello.com.br';

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    };
    window.addEventListener('resize', handleResize);
    
    const timer = setTimeout(() => setShowConfetti(false), 5000);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timer);
    };
  }, []);

  const handleAccessMembersArea = () => {
    window.open(MEMBERS_AREA_URL, '_blank');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FBF4FF] via-white to-[#FAE0D5] relative overflow-hidden">
      {showConfetti && (
        <Confetti
          width={windowSize.width}
          height={windowSize.height}
          recycle={false}
          numberOfPieces={200}
          colors={['#8F7AC5', '#D98DAE', '#F2ACAA', '#FAE0D5', '#30244F']}
        />
      )}

      {/* Header */}
      <header className="py-6 px-4">
        <div className="max-w-2xl mx-auto flex justify-center">
          <Logo variant="v2SonoRotina" size="md" />
        </div>
      </header>

      {/* Content */}
      <main className="max-w-2xl mx-auto px-4 py-8 pb-24">
        <motion.div
          className="text-center space-y-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Success Icon */}
          <motion.div
            className="relative inline-block"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', delay: 0.3 }}
          >
            <div className="w-32 h-32 bg-gradient-to-br from-[#8F7AC5] to-[#D98DAE] rounded-full flex items-center justify-center mx-auto shadow-lg">
              <Check className="w-16 h-16 text-white" strokeWidth={3} />
            </div>
            <motion.div
              className="absolute -top-2 -right-2"
              animate={{ rotate: [0, 10, -10, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <Sparkles className="w-8 h-8 text-[#F2ACAA]" />
            </motion.div>
          </motion.div>

          {/* Message */}
          <div className="space-y-4">
            <h1 className="text-3xl md:text-4xl font-bold text-[#30244F]">
              Parabéns, mamãe! 💜
            </h1>
            <p className="text-xl text-[#6B5B8C]">
              Você completou o registro de rotina!
            </p>
          </div>

          {/* Info Card - Área de Membros */}
          <motion.div
            className="bg-white rounded-3xl p-8 shadow-md max-w-md mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <div className="flex items-center justify-center gap-3 mb-4">
              <Calendar className="w-6 h-6 text-[#8F7AC5]" />
              <span className="text-lg font-semibold text-[#30244F]">Próximos passos</span>
            </div>
            
            <div className="space-y-4 text-left">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-[#D98DAE]/30 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-[#8F7AC5] font-semibold text-sm">1</span>
                </div>
                <div>
                  <p className="text-[#30244F] font-medium">Análise do registro</p>
                  <p className="text-sm text-[#9890A8]">Vou analisar todos os dados com carinho</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-[#D98DAE]/30 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-[#8F7AC5] font-semibold text-sm">2</span>
                </div>
                <div>
                  <p className="text-[#30244F] font-medium">Plano personalizado</p>
                  <p className="text-sm text-[#9890A8]">Montarei um plano especial para vocês</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-[#D98DAE]/30 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-[#8F7AC5] font-semibold text-sm">3</span>
                </div>
                <div>
                  <p className="text-[#30244F] font-medium">Acesse sua Área de Membros</p>
                  <p className="text-sm text-[#9890A8]">
                    Em até <strong>7 dias</strong>, seu plano estará disponível em{' '}
                    <strong className="text-[#8F7AC5]">Materiais → Apps 7 dias</strong>
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Destaque - Área de Membros */}
          <motion.div
            className="bg-gradient-to-r from-[#8F7AC5] to-[#D98DAE] rounded-2xl p-6 max-w-md mx-auto text-white shadow-lg"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.7 }}
          >
            <div className="flex items-center gap-3 mb-3">
              <MoonIcon variant="white" size="sm" />
              <span className="font-semibold">Onde encontrar seu plano?</span>
            </div>
            <p className="text-white/90 text-sm mb-4">
              Acesse a <strong>Área de Membros</strong> e navegue até{' '}
              <strong>Materiais → Apps 7 dias</strong> para visualizar seu plano de sono personalizado.
            </p>
            <Button
              onClick={handleAccessMembersArea}
              className="w-full bg-white text-[#8F7AC5] hover:bg-white/90 font-semibold"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Acessar Área de Membros
            </Button>
          </motion.div>

          {/* Encouragement */}
          <motion.div
            className="flex items-center justify-center gap-2 text-[#D98DAE]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1 }}
          >
            <Heart className="w-5 h-5" fill="currentColor" />
            <span className="text-sm font-medium">De mãe pra mãe, vamos juntas!</span>
            <Heart className="w-5 h-5" fill="currentColor" />
          </motion.div>

          {/* Action Buttons */}
          <motion.div
            className="flex flex-col sm:flex-row gap-3 justify-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2 }}
          >
            <Button
              size="lg"
              onClick={handleAccessMembersArea}
              className="bg-[#8F7AC5] hover:bg-[#30244F] text-white px-8 py-6 rounded-full shadow-lg"
            >
              <ExternalLink className="w-5 h-5 mr-2" />
              Ir para Área de Membros
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => navigate('/')}
              className="border-[#8F7AC5] text-[#8F7AC5] hover:bg-[#8F7AC5]/10 px-8 py-6 rounded-full"
            >
              <Home className="w-5 h-5 mr-2" />
              Voltar ao Início
            </Button>
          </motion.div>
        </motion.div>
      </main>

      {/* Decorative elements */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#FAE0D5]/50 to-transparent pointer-events-none" />
    </div>
  );
};

export default CompletionPage;
